import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { checkmarkCircleFilled } from '@ld.consumer-portal/data-uri';
import { ProgressStep } from '@ld.consumer-portal/models';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ld-progress-stepper',
  templateUrl: './progress-stepper.component.html',
  styleUrl: './progress-stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgIconComponent, TranslocoDirective],
})
export class ProgressStepperComponent {
  readonly steps = input.required<ProgressStep[]>();
  readonly currentStepIndex = input<number>(0);
  readonly allowNavigation = input(true);
  readonly compact = input(false);
  readonly forceMobile = input(false);

  readonly stepClick = output<ProgressStep>();

  protected readonly mobileClass = computed(() => {
    if (this.forceMobile()) return 'overflow-hidden block';
    return this.compact() ? 'overflow-hidden block md:hidden' : 'overflow-hidden lg:hidden';
  });

  protected readonly desktopClass = computed(() => {
    if (this.forceMobile()) return 'hidden';
    return this.compact() ? 'hidden' : 'hidden lg:block py-6';
  });

  protected readonly checkmarkIcon = checkmarkCircleFilled;

  protected readonly stepsWithPosition = computed(() => {
    const steps = this.steps();
    return steps.map((step, index) => ({
      ...step,
      isFirst: index === 0,
      isLast: index === steps.length - 1,
      index,
    }));
  });

  protected readonly isMobileExpanded = signal(false);

  protected readonly currentStep = computed(() => {
    const steps = this.steps();
    const currentIndex = steps.findIndex((s) => s.status === 'current');
    if (currentIndex >= 0) {
      return { step: steps[currentIndex], index: currentIndex };
    }
    const lastCompleted = steps.filter((s) => s.status === 'completed').length - 1;
    return lastCompleted >= 0 ? { step: steps[lastCompleted], index: lastCompleted } : { step: steps[0], index: 0 };
  });

  protected readonly progressPercent = computed(() => {
    const steps = this.steps();
    if (steps.length <= 1) return 100;
    const completedCount = steps.filter((s) => s.status === 'completed').length;
    return Math.round((completedCount / (steps.length - 1)) * 100);
  });

  protected readonly completedCount = computed(() => {
    return this.steps().filter((s) => s.status === 'completed').length;
  });

  protected toggleMobileExpanded(): void {
    this.isMobileExpanded.update((v) => !v);
  }

  protected onStepClick(step: ProgressStep): void {
    if (step.status === 'completed' && this.allowNavigation()) {
      this.stepClick.emit(step);
    }
  }

  protected getProgressBarClass(step: ProgressStep, position: 'left' | 'right'): string {
    const baseClass = 'h-1 flex-1 min-w-0';
    const marginClass = position === 'left' ? '-ml-px' : '-mr-px';

    if (position === 'left') {
      if (step.status === 'completed' || step.status === 'current') {
        return `${baseClass} ${marginClass} bg-ld-primary`;
      }
      return `${baseClass} ${marginClass} bg-ld-neutral-200`;
    }

    if (step.status === 'completed') {
      return `${baseClass} ${marginClass} bg-ld-primary`;
    }
    return `${baseClass} ${marginClass} bg-ld-neutral-200`;
  }
}
